import { Vehicle, VehicleFuelType } from '@api/index';
import { createSlice } from '@reduxjs/toolkit';
import { TableViewTogglesViewType } from '@rio-cloud/rio-uikit/components/table/TableViewToggles';
import TableViewTogglesValues from '@rio-cloud/rio-uikit/TableViewToggles';
import _ from 'lodash';
import moment from 'moment';

import { UiStatePart } from '../../setup/types';
import { DateRange, Driver, HydratedEntity } from '../../types';

export type SidebarData = { id: string; level: number; childrenIds: string[] };

export type UiState = {
    dateRange: DateRange;
    sidebar?: { type: string; data: SidebarData };
    filteredSegment?: DateRange;
    columnToggleDialogVisible: boolean;
    isGraphCollapsed: boolean;
    tableViewType: TableViewTogglesViewType;
    isMobile: boolean;
};

export const defaultState: UiState = {
    isGraphCollapsed: false,
    dateRange: {
        start: moment()
            .startOf('day')
            .subtract(10, 'days')
            .toDate(),
        end: moment()
            .endOf('day')
            .toDate(),
    },
    sidebar: undefined,
    columnToggleDialogVisible: false,
    filteredSegment: undefined,
    tableViewType: TableViewTogglesValues.VIEW_TYPE_TABLE,
    isMobile: false,
};

const uiSlice = createSlice({
    name: 'ui',
    initialState: defaultState,
    reducers: {
        setFilteredSegment(state, { payload }) {
            state.filteredSegment = payload;
            state.sidebar = undefined;
        },
        showSidebar(state, { payload: { data, type } }) {
            if (data) {
                state.sidebar = { data, type };
            }
        },
        hideSidebar(state) {
            return { ...state, sidebar: undefined };
        },
        setIsGraphCollapsed(state, { payload: isCollapsed }) {
            return { ...state, isGraphCollapsed: isCollapsed };
        },
        setDateRange(state, { payload }: { payload: DateRange }) {
            state.dateRange = payload;
            state.filteredSegment = undefined;
            state.sidebar = undefined;
        },
        setTableViewType(state, { payload: viewType }) {
            return { ...state, tableViewType: viewType };
        },

        setIsMobile(state) {
            return { ...state, tableViewType: TableViewTogglesValues.VIEW_TYPE_MULTI_CARDS, isMobile: true };
        },
    },
});

export const {
    setFilteredSegment,
    showSidebar,
    hideSidebar,
    setDateRange,
    setIsGraphCollapsed,
    setTableViewType,
    setIsMobile,
} = uiSlice.actions;

export default uiSlice.reducer;

export const isSidebarVisible = (state: UiStatePart, type: string) =>
    !!state.ui.sidebar && state.ui.sidebar.type === type;
export const sidebarData = (state: UiStatePart, key: string) => {
    if (state.ui.sidebar?.type === key) {
        return _.get(state, 'ui.sidebar.data');
    }
    return undefined;
};

export const driverSidebarData = (state: UiStatePart) => {
    const data = sidebarData(state, '/driver') as
        | (SidebarData & { vehicles?: Vehicle[]; drivers?: Driver[]; children?: HydratedEntity[] })
        | undefined;
    if (!data) return undefined;

    const vehicles = data.vehicles ?? [];
    const drivers = data.drivers ?? [];
    const electricVehicles = vehicles.filter(v => v.fuelType === VehicleFuelType.ELECTRIC);
    const fuelVehicles = vehicles.filter(v => v.fuelType !== VehicleFuelType.ELECTRIC);

    return {
        ...data,
        driverIds: drivers.map(d => d.id),
        electricVehicles,
        electricVehicleIds: electricVehicles.map(v => v.id),
        fuelVehicleIds: fuelVehicles.map(v => v.id),
        fuelVehicles,
        containsElectricVehicle: vehicles.some(v => v.fuelType === VehicleFuelType.ELECTRIC),
        containsCombustionVehicle: vehicles.some(v => v.fuelType !== VehicleFuelType.ELECTRIC),
        isVehiclesNameClickable: (data.children?.length ?? 0) > 1,
    };
};

export const isGraphCollapsed = (state: UiStatePart) => !!_.get(state, 'ui.isGraphCollapsed');
export const getTableViewType = (state: UiStatePart) => state.ui.tableViewType;
export const isMobile = (state: UiStatePart) => state.ui.isMobile;
